import React, { useState, useEffect } from "react";
import { Home, ArrowLeft, Search, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Meta from "./components/Meta.js";

export default function Modern404Page() {
  const [isVisible, setIsVisible] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);

  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  useEffect(() => {
    setIsVisible(true);

    // Create floating elements
    const elements = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2,
    }));
    setFloatingElements(elements);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <>
      <Meta
        title="404 Page Not Found - Govind Sinha & Co."
        description="This does not seem to be the page you are looking for."
        canonical="https://cagovindsinha.com/404"
      />
      <div className="relative flex items-center justify-center min-h-screen p-4 overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-yellow-50">
        {/* Floating background elements */}
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className="absolute w-20 h-20 rounded-full opacity-20"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              background: `linear-gradient(45deg, ${
                element.id % 3 === 0
                  ? "#fbbf24"
                  : element.id % 3 === 1
                  ? "#3b82f6"
                  : "#8b5cf6"
              }, ${
                element.id % 3 === 0
                  ? "#f59e0b"
                  : element.id % 3 === 1
                  ? "#1d4ed8"
                  : "#7c3aed"
              })`,
              animation: `float ${element.duration}s ease-in-out infinite`,
              animationDelay: `${element.delay}s`,
            }}
          />
        ))}

        {/* Main content */}
        <div
          className={`max-w-4xl mx-auto text-center transform transition-all duration-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          {/* 404 Number */}
          <div className="relative mb-8">
            <h1 className="text-9xl md:text-[12rem] font-black bg-gradient-to-r from-blue-600 via-purple-600 to-yellow-500 bg-clip-text text-transparent leading-none">
              404
            </h1>
            <div className="absolute inset-0 text-9xl md:text-[12rem] font-black text-blue-200 opacity-30 blur-sm">
              404
            </div>
          </div>

          {/* Title */}
          <div className="mb-6 space-y-2">
            <h2 className="mb-2 text-3xl font-bold text-gray-800 md:text-4xl">
              Oops! Page not found
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600 md:text-xl">
              The page you're looking for seems to have wandered off into the
              digital void. Don't worry, even the best explorers sometimes take
              a wrong turn!
            </p>
          </div>

          {/* Decorative elements */}
          <div className="flex items-center justify-center mb-8 space-x-4">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce"></div>
            <div
              className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-3 h-3 bg-purple-400 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col items-center justify-center gap-4 mb-8 sm:flex-row">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 px-6 py-3 font-medium text-white transition-all duration-300 transform rounded-full shadow-lg group bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105 hover:shadow-xl"
            >
              <Home className="w-5 h-5 transition-transform group-hover:rotate-12" />
              Go Home
            </button>

            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 px-6 py-3 font-medium text-white transition-all duration-300 transform rounded-full shadow-lg group bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600 hover:scale-105 hover:shadow-xl"
            >
              <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
              Go Back
            </button>
          </div>

          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 mx-auto mt-6 text-gray-500 transition-colors group hover:text-gray-700"
          >
            <RefreshCw className="w-4 h-4 transition-transform duration-500 group-hover:rotate-180" />
            Refresh page
          </button>
        </div>

        <style jsx>{`
          @keyframes float {
            0%,
            100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
            }
          }
        `}</style>
      </div>
    </>
  );
}
